import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, Calendar, FileText, Filter, ChevronDown, RefreshCw, Eye } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../MesrequetesStyles/BulletinsPaie.css";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";
import { fetchMyPayslipsRequest } from "../../../../redux/actions/payslipActions";

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("fr-FR");
};

const splitPeriod = (periodLabel, importedAt) => {
    if (periodLabel) {
        const parts = periodLabel.split(" ");
        if (parts.length >= 2) {
            return {
                month: parts.slice(0, parts.length - 1).join(" "),
                year: parts[parts.length - 1],
            };
        }
    }

    if (importedAt) {
        const date = new Date(importedAt);
        if (!Number.isNaN(date.getTime())) {
            return {
                month: date.toLocaleString("fr-FR", { month: "long" }).replace(/^./, (value) => value.toUpperCase()),
                year: String(date.getFullYear()),
            };
        }
    }

    return { month: "Non détectée", year: "" };
};

export default function BulletinsPaie() {
    const dispatch = useDispatch();
    const { myPayslips, loading, error } = useSelector((state) => state.payslips);

    const [searchMonth, setSearchMonth] = useState("");
    const [searchYear, setSearchYear] = useState("");
    const [showFilters, setShowFilters] = useState(true);

    useEffect(() => {
        dispatch(fetchMyPayslipsRequest());
    }, [dispatch]);

    const bulletins = useMemo(() => {
        return (myPayslips || []).map((item) => {
            const period = splitPeriod(item.periodLabel, item.importedAt);
            return {
                ...item,
                month: period.month,
                year: period.year,
                displayDate: formatDate(item.importedAt),
                displaySize: formatSize(item.fileSize),
            };
        });
    }, [myPayslips]);

    const mois = useMemo(() => {
        return [...new Set(bulletins.map((item) => item.month).filter(Boolean))];
    }, [bulletins]);

    const annees = useMemo(() => {
        return [...new Set(bulletins.map((item) => item.year).filter(Boolean))].sort((a, b) => Number(b) - Number(a));
    }, [bulletins]);

    const bulletinsFiltres = useMemo(() => {
        return bulletins.filter((item) => {
            const matchMonth = !searchMonth || item.month === searchMonth;
            const matchYear = !searchYear || item.year === searchYear;
            return matchMonth && matchYear;
        });
    }, [bulletins, searchMonth, searchYear]);

    const resetFilters = () => {
        setSearchMonth("");
        setSearchYear("");
    };

    const openPdf = async (relativeUrl, fileName, download = false) => {
        try {
            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const response = await getBlobRequest({
                url: `${API_BASE_URL}${relativeUrl}${download ? "?download=true" : ""}`,
                token,
                extraHeaders: { uid },
            });

            const blob = new Blob([response.data], { type: "application/pdf" });
            const blobUrl = window.URL.createObjectURL(blob);

            if (download) {
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = fileName || "bulletin.pdf";
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
                return;
            }

            window.open(blobUrl, "_blank", "noopener,noreferrer");
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        } catch (downloadError) {
            Swal.fire({
                icon: "error",
                title: "Téléchargement impossible",
                text: "Le bulletin de paie n'a pas pu être récupéré.",
                confirmButtonText: "OK",
            });
        }
    };

    return (
        <div className="bpPage">
            <div className="bpWrap">
                <div className="bpHeader">
                    <div className="bpHeadLeft">
                        <div className="bpHeadIcon">
                            <FileText size={18} />
                        </div>
                        <div>
                            <h1>Mes bulletins de paie</h1>
                            <p>Consultez et téléchargez vos bulletins</p>
                        </div>
                    </div>

                    <div className="bpHeadRight">
                        <div className="bpTotalBox">
                            <div className="bpTotalLabel">Total</div>
                            <div className="bpTotalVal">{bulletinsFiltres.length}</div>
                        </div>
                    </div>
                </div>

                <div className="bpCard">
                    <div className="bpControls">
                        <div className="bpSearch">
                            <Search size={16} />
                            <input
                                value={searchMonth || searchYear ? `Filtré : ${searchMonth || "Tous mois"} • ${searchYear || "Toutes années"}` : ""}
                                placeholder="Choisissez un mois et une année..."
                                readOnly
                            />
                        </div>

                        <button type="button" className="bpFilterBtn" onClick={() => setShowFilters((value) => !value)}>
                            <Filter size={18} />
                            Filtres
                            <ChevronDown size={16} className={`bpChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="bpFilters">
                            <div className="bpField">
                                <label>Mois</label>
                                <div className="bpSelectWrap">
                                    <Calendar size={16} />
                                    <select value={searchMonth} onChange={(event) => setSearchMonth(event.target.value)}>
                                        <option value="">Tous les mois</option>
                                        {mois.map((month) => (
                                            <option key={month} value={month}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="bpField">
                                <label>Année</label>
                                <div className="bpSelectWrap">
                                    <Calendar size={16} />
                                    <select value={searchYear} onChange={(event) => setSearchYear(event.target.value)}>
                                        <option value="">Toutes les années</option>
                                        {annees.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="bpField bpField--actions">
                                <label>&nbsp;</label>
                                <button type="button" className="bpResetBtn" onClick={resetFilters}>
                                    <RefreshCw size={16} />
                                    Réinitialiser
                                </button>
                            </div>
                        </div>
                    )}

                    {(searchMonth || searchYear) && (
                        <div className="bpActiveFilters">
                            <span className="bpActiveLabel">Filtres actifs:</span>
                            {searchMonth && <span className="bpTag">{searchMonth}</span>}
                            {searchYear && <span className="bpTag">{searchYear}</span>}
                        </div>
                    )}
                </div>

                <div className="bpCard bpCard--noPad">
                    <div className="bpListHeader">
                        <div className="bpListHeaderTitle">
                            <FileText size={18} />
                            Liste des bulletins de paie
                        </div>
                    </div>

                    {loading && (
                        <div className="bpEmpty">
                            <FileText size={38} />
                            <p>Chargement des bulletins de paie…</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="bpEmpty">
                            <FileText size={38} />
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && !error && bulletinsFiltres.length === 0 && (
                        <div className="bpEmpty">
                            <FileText size={38} />
                            <p>Aucun bulletin de paie disponible</p>
                        </div>
                    )}

                    {!loading && !error && bulletinsFiltres.length > 0 && (
                        <div className="bpList">
                            {bulletinsFiltres.map((bulletin) => (
                                <div key={bulletin.id} className="bpRow">
                                    <div className="bpRowLeft">
                                        <div className="bpRowIcon">
                                            <FileText size={18} />
                                        </div>

                                        <div className="bpRowInfo">
                                            <div className="bpRowTitle">
                                                {bulletin.periodLabel || `Bulletin de ${bulletin.month} ${bulletin.year}`}
                                            </div>

                                            <div className="bpRowMeta">
                                                <span className="bpMetaItem">
                                                    <FileText size={14} />
                                                    {bulletin.fileName || bulletin.sourceFileName || "Bulletin.pdf"}
                                                </span>
                                            </div>

                                            <div className="bpRowMeta">
                                                <span className="bpMetaItem">
                                                    <Calendar size={14} />
                                                    {bulletin.displayDate}
                                                </span>
                                                <span className="bpBadge">{bulletin.displaySize}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bpRowRight" style={{ display: "flex", gap: 8 }}>
                                        <button type="button" className="bpDownloadBtn" onClick={() => openPdf(bulletin.downloadUrl, bulletin.fileName, false)}>
                                            <Eye size={16} />
                                            Voir
                                        </button>
                                        <button type="button" className="bpDownloadBtn" onClick={() => openPdf(bulletin.downloadUrl, bulletin.fileName, true)}>
                                            <Download size={16} />
                                            Télécharger
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
